import {PriceRequest} from '../services/api/price'

import {Nullable, PriceFormula} from '../types'

type StaggeredPriceArray = Array<{price: number}>
type PriceValue = Nullable<number | StaggeredPriceArray> | undefined

const createStaggeredPriceGetter = (
  priceProperty: keyof PriceRequest,
  thresholdProperty: string
) => {
  return (priceRequest: Partial<PriceRequest>, threshold: number): number => {
    const priceValue = priceRequest[priceProperty] as PriceValue

    if (typeof priceValue === 'number') {
      return priceValue
    }

    if (
      typeof priceValue === 'object' &&
      priceValue &&
      Array.isArray(priceValue) &&
      priceValue.length > 0
    ) {
      let item = priceValue[0]
      for (let i = 0; i < priceValue.length; i += 1) {
        const thresholdValue = (priceValue[i] as Record<string, number>)[thresholdProperty]
        if (threshold < thresholdValue) {
          break
        }
        item = priceValue[i]
      }

      return item.price
    }

    return 0
  }
}

export const getStaggeredVolumePriceItem = createStaggeredPriceGetter('volumePrice', 'volume')

export const getStaggeredWeightPriceItem = createStaggeredPriceGetter('weightPrice', 'weight')

export const getTotalWeightInKg = (
  priceRequest: Partial<PriceRequest>,
  {volume = 0, boundingBox = 0, quantity = 1}: ModelProperties
): number => {
  if (priceRequest.formula === PriceFormula.THREE_D_PRINTING) {
    return (volume * (priceRequest.density || 0) * quantity) / 1000
  } else {
    return (boundingBox * (priceRequest.density || 0) * quantity) / 1000
  }
}

const roundNumber = (value: number) => Math.round(value * 100) / 100

type ModelProperties = {
  volume?: number
  boundingBox?: number
  surfaceArea?: number
  quantity?: number
  setupCount?: number
}

export const calculateModelGeometryPrice = (
  priceRequest: Partial<PriceRequest>,
  modelProperties: ModelProperties
) => {
  const {volume = 0, boundingBox = 0, surfaceArea = 0} = modelProperties

  if (!priceRequest.volumePrice && !priceRequest.weightPrice) {
    return 0
  }

  const volumePrice = priceRequest.weightPrice
    ? (() => {
        const totalWeightInKg = getTotalWeightInKg(priceRequest, modelProperties)
        return (
          volume *
          getStaggeredWeightPriceItem(priceRequest, totalWeightInKg) *
          (priceRequest.density || 0)
        )
      })()
    : volume * getStaggeredVolumePriceItem(priceRequest, volume)

  return (
    volumePrice +
    boundingBox * (priceRequest.boundingBoxPrice || 0) +
    surfaceArea * (priceRequest.surfacePrice || 0)
  )
}

export const calculateModelMachiningPrice = (
  priceRequest: Partial<PriceRequest>,
  modelProperties: ModelProperties
) => {
  const {volume = 0, boundingBox = 0, surfaceArea = 0} = modelProperties

  if (
    !priceRequest.machiningCostPerHour ||
    !priceRequest.machiningMaterialRemovalRate ||
    !priceRequest.finishingMaterialRemovalRate ||
    !priceRequest.density ||
    !priceRequest.weightPrice
  ) {
    return 0
  }

  const totalWeightInKg = getTotalWeightInKg(priceRequest, modelProperties)

  const materialCost =
    boundingBox * priceRequest.density * getStaggeredWeightPriceItem(priceRequest, totalWeightInKg)
  const roughMachiningCost =
    (boundingBox - volume) *
    priceRequest.machiningMaterialRemovalRate *
    priceRequest.machiningCostPerHour *
    (1 / 60)
  const finishingCost =
    surfaceArea *
    priceRequest.finishingMaterialRemovalRate *
    priceRequest.machiningCostPerHour *
    (1 / 60)
  const setupCount = modelProperties.setupCount ?? 1
  const numberOfRefixtures = Math.max(0, setupCount - 1)
  const refixtureTimeInHours =
    (numberOfRefixtures * (priceRequest.machineRefixtureTimePerSetup ?? 0)) / 60
  const refixtureTimeCost = refixtureTimeInHours * priceRequest.machiningCostPerHour
  const camProgrammingCost = setupCount * (priceRequest.camProgrammingFeePerSetup ?? 0)

  return materialCost + roughMachiningCost + finishingCost + refixtureTimeCost + camProgrammingCost
}

// Note: we calculate and return rounded values for all returned prices (to be in sync with the backend)
export const calculatePrices = (
  priceRequest: Partial<PriceRequest>,
  modelPrice: number,
  quantity: number,
  pricePremiumShare: number,
  modelFee: number,
  ccMargin: number,
  profitShare: number
): {
  piecePrice: number
  quantityDiscountedPiecePrice: number
  totalPricePerFile: number
  totalPriceWithCCMarginAndModelFee: number
  totalProfitShare: number
  totalCustomerPrice: number
  totalCustomerPriceV2: number
  invoiceableAmountV2: number
} => {
  const piecePrice = Math.max(
    (priceRequest.baseUnitPrice || 0) + modelPrice,
    priceRequest.minimumUnitPrice || 0
  )

  const quantityDiscountedPiecePrice =
    Math.max(
      quantity ** (priceRequest.quantityDiscount || 0),
      priceRequest.quantityDiscountCap || 0
    ) * piecePrice

  const totalPricePerFile = roundNumber(
    Math.max(
      (priceRequest.basePrice || 0) + quantity * quantityDiscountedPiecePrice,
      priceRequest.minimumPrice || 0
    )
  )

  // V1 pricing: Total price per file + production premium (30%)
  const totalCustomerPrice = roundNumber(totalPricePerFile * (1 + pricePremiumShare))

  // V2 pricing: (Total price per file + 33% CC Margin + 1.2 USD per file) + 30% profit share
  const numberOfFiles = 1 // fixed for now - does not make sense to make this dynamic for the user
  const ccMarginFee = roundNumber(totalPricePerFile * ccMargin)
  const totalPriceWithCCMargin = roundNumber(totalPricePerFile + ccMarginFee)
  const ccModelFee = roundNumber(numberOfFiles * modelFee)

  const totalPriceWithCCMarginAndModelFee =
    totalPricePerFile > 0 ? roundNumber(totalPriceWithCCMargin + ccModelFee) : 0

  const totalProfitShare = roundNumber(totalPriceWithCCMarginAndModelFee * profitShare)

  const totalCustomerPriceV2 = roundNumber(totalPriceWithCCMarginAndModelFee + totalProfitShare)

  const invoiceableAmountV2 = roundNumber(totalPricePerFile + totalProfitShare / 2)

  return {
    piecePrice,
    quantityDiscountedPiecePrice,
    totalPricePerFile,
    totalPriceWithCCMarginAndModelFee,
    totalProfitShare,
    totalCustomerPrice,
    totalCustomerPriceV2,
    invoiceableAmountV2,
  }
}

export const calculatePricesForShipping = (
  rawShippingPrice: number,
  ccMargin: number,
  profitShare: number
) => {
  if (rawShippingPrice <= 0) {
    return {
      totalCustomerPriceV2: 0,
      invoiceableAmountV2: 0,
    }
  }
  const priceIncludingCCMargin = roundNumber(rawShippingPrice * (1 + ccMargin))
  const totalProfitShare = roundNumber(priceIncludingCCMargin * profitShare)

  const totalCustomerPriceV2 = roundNumber(priceIncludingCCMargin + totalProfitShare)

  const invoiceableAmountV2 = roundNumber(rawShippingPrice + totalProfitShare / 2)

  return {
    totalCustomerPriceV2,
    invoiceableAmountV2,
  }
}

export const calculatePricesForMinProductionPrice = (
  rawShippingPrice: number,
  ccMargin: number,
  profitShare: number,
  modelFee: number
) => {
  if (rawShippingPrice <= 0) {
    return {
      totalCustomerPriceV2: 0,
      invoiceableAmountV2: 0,
    }
  }
  const numberOfFiles = 1 // fixed - final customer price takes the number of models into account. The minimum number of models is 1.
  const ccModelFee = roundNumber(numberOfFiles * modelFee)

  const priceIncludingCCMargin = roundNumber(rawShippingPrice * (1 + ccMargin))
  const priceIncludingCCMarginAndModelFee = priceIncludingCCMargin + ccModelFee

  const totalProfitShare = roundNumber(priceIncludingCCMarginAndModelFee * profitShare)

  const totalCustomerPriceV2 = roundNumber(priceIncludingCCMarginAndModelFee + totalProfitShare)

  const invoiceableAmountV2 = roundNumber(rawShippingPrice + totalProfitShare / 2)

  return {
    totalCustomerPriceV2,
    invoiceableAmountV2,
  }
}

export const calculatePricesForAdditionalCost = (additionalCostPrice: number, ccMargin: number) => {
  if (additionalCostPrice <= 0) {
    return {
      totalCustomerPriceV2: 0,
      invoiceableAmountV2: 0,
    }
  }

  const ccMarginPrice = roundNumber(additionalCostPrice * ccMargin)
  const totalCustomerPriceV2 = additionalCostPrice
  const invoiceableAmountV2 = roundNumber(additionalCostPrice - ccMarginPrice)

  return {
    totalCustomerPriceV2,
    invoiceableAmountV2,
  }
}
